<template>
    <div class="pool-quotas py-3">
        <v-chip v-if="!canView" small outlined>
            <v-icon left small>mdi-lock-outline</v-icon>
            Manager access required
        </v-chip>
        <span v-else-if="state.loading" class="text--secondary" role="status">Loading quotas…</span>
        <span v-else-if="state.error" class="error--text" role="status">Could not load cloud quotas.</span>
        <span v-else-if="!state.clusters.length" class="text--secondary">-</span>
        <template v-else>
            <div class="d-flex align-center">
                <div class="flex-grow-1" style="min-width: 0">
                    <div v-for="cluster in state.clusters" :key="cluster.tcid" class="quota-summary mb-1">
                        <div v-if="state.clusters.length > 1" class="text-caption text-truncate" :title="clusterName(cluster)">{{ clusterName(cluster) }}</div>
                        <div class="d-flex align-center">
                            <div
                                v-for="resource in summary(cluster)"
                                :key="resource.key"
                                class="quota-summary-metric"
                                :title="`${resource.label}: ${resource.text}`">
                                <div class="text-caption text--secondary">{{ resource.label }}</div>
                                <v-progress-linear
                                    v-if="resource.available && !resource.unlimited"
                                    :value="resource.percent"
                                    :color="resource.color"
                                    :aria-label="`${clusterName(cluster)}: ${resource.label}, ${resource.text}`"
                                    rounded
                                    height="4" />
                                <div class="text-caption">
                                    {{ resource.available ? (resource.unlimited ? 'Unlimited' : Math.round(resource.percent) + '%') : 'Unknown' }}
                                </div>
                            </div>
                            <v-icon
                                v-if="cluster.quota_error"
                                small
                                color="warning"
                                title="Quota refresh failed; showing last available snapshot"
                                aria-label="Quota snapshot may be stale">
                                mdi-alert-outline
                            </v-icon>
                        </div>
                    </div>
                </div>
                <v-btn text small color="primary" class="ml-2 flex-shrink-0" @click="dialog = true">
                    All usage
                    <v-icon right small>mdi-open-in-new</v-icon>
                </v-btn>
            </div>
            <v-dialog v-model="dialog" max-width="960" persistent scrollable>
                <v-card v-if="dialog" flat>
                    <v-card-title class="secondary--text">
                        Integration quota usage
                        <v-spacer />
                        <v-btn icon aria-label="Close integration quota usage" @click="dialog = false"><v-icon>clear</v-icon></v-btn>
                    </v-card-title>
                    <v-card-text>
                        <p class="text--secondary">Cloud project quotas are shared by resource pools using the same account; they are not per-pool usage.</p>
                        <section v-for="cluster in state.clusters" :key="cluster.tcid" class="mb-6">
                            <h3 class="subtitle-1 font-weight-bold">{{ clusterName(cluster) }}</h3>
                            <div class="text-caption text--secondary">{{ cluster.cloud_provider }} · {{ cluster.cloud_region }}</div>
                            <div v-if="cluster.quota_error" class="warning--text text-caption">Quota refresh failed; showing last available snapshot.</div>
                            <div v-if="cluster.quota_observed_at" class="text-caption text--secondary">
                                Last checked: {{ observedAt(cluster.quota_observed_at) }}
                            </div>
                            <v-row class="mt-1">
                                <v-col v-for="group in groups(cluster)" :key="group.key" cols="12" md="4">
                                    <v-sheet outlined rounded class="pa-4 quota-section">
                                        <h4 class="subtitle-2 mb-4">{{ group.label }}</h4>
                                        <div v-if="group.unavailable" class="text-caption text--secondary">Quota data unavailable</div>
                                        <div v-for="resource in group.resources" :key="resource.key" class="mb-4">
                                            <div class="text-caption font-weight-medium">{{ resource.label }}</div>
                                            <div class="text-caption text--secondary mb-1">{{ resource.text }}</div>
                                            <v-progress-linear
                                                v-if="resource.available && !resource.unlimited"
                                                :value="resource.percent"
                                                :color="resource.color"
                                                :aria-label="`${group.label}: ${resource.label}, ${resource.text}`"
                                                rounded
                                                height="6" />
                                            <div v-else class="text-caption text--secondary">
                                                {{ resource.unlimited ? 'Unlimited quota' : 'Usage unavailable' }}
                                            </div>
                                        </div>
                                    </v-sheet>
                                </v-col>
                            </v-row>
                        </section>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer />
                        <v-btn text color="primary" @click="dialog = false">Close</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </template>
    </div>
</template>

<script>
import { quotaGroups, quotaSummary } from './resourcePoolQuotas'

export default {
    name: 'ResourcePoolQuotas',
    props: {
        canView: Boolean,
        state: {
            type: Object,
            default: () => ({ loading: false, error: false, clusters: [] })
        }
    },
    data: () => ({ dialog: false }),
    methods: {
        groups: quotaGroups,
        summary: quotaSummary,
        clusterName: cluster => cluster.cluster_name || cluster.cloud_region || 'Cloud project',
        observedAt(value) {
            const date = new Date(/(?:Z|[+-]\d{2}:?\d{2})$/.test(value) ? value : `${value}Z`)
            return Number.isNaN(date.getTime()) ? 'Unknown' : date.toLocaleString()
        }
    }
}
</script>

<style scoped>
.pool-quotas {
    min-width: 210px;
}
.quota-summary-metric {
    flex: 1;
    min-width: 0;
    margin-right: 12px;
}
.quota-summary-metric:last-child {
    margin-right: 0;
}
.quota-section {
    height: 100%;
}
</style>
